import PropTypes from "prop-types";
import {
  CartesianGrid,
  Line,
  LineChart as RechartsLineChart,
  XAxis,
  YAxis,
} from "recharts";

import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { Skeleton } from "@/components/ui/skeleton";
import ChartFilterBadges from "../chart-filter-badges";

// shadcn Chart + recharts replacement for the previous Victory wrapper.
// The series colour ("--color-y") resolves against the theme tokens
// declared in `index.css` (--chart-1 by default, brand blue); callers
// can override via `style.line.data.stroke` to preserve the old API.

const CHART_CONFIG = {
  y: {
    label: "",
    color: "var(--chart-1)",
  },
};

LineChart.propTypes = {
  title: PropTypes.string,
  noDataMessage: PropTypes.string,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      x: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      y: PropTypes.number,
    }),
  ),
  style: PropTypes.shape({
    chart: PropTypes.object,
    line: PropTypes.object,
    tooltip: PropTypes.object,
    xLabel: PropTypes.object,
    yLabel: PropTypes.object,
    title: PropTypes.object,
    activeFilters: PropTypes.object,
    noDataMessage: PropTypes.object,
  }),
  xLabel: PropTypes.string,
  yLabel: PropTypes.string,
  tooltipLabel: PropTypes.string,
  isLoading: PropTypes.bool,
  showAllTicks: PropTypes.bool,
  activeFilters: PropTypes.array,
};

export default function LineChart({
  title,
  noDataMessage,
  data,
  style,
  xLabel,
  yLabel,
  tooltipLabel,
  isLoading,
  showAllTicks,
  activeFilters,
}) {
  const dataLength = data?.length ?? 0;

  let middleLabelCount;
  if (dataLength < 20) middleLabelCount = 1;
  else if (dataLength < 40) middleLabelCount = 2;
  else middleLabelCount = 3;

  const firstIndex = 0;
  const lastIndex = dataLength - 1;
  const interval = Math.floor(dataLength / (middleLabelCount + 1));
  const middleIndices = Array.from(
    { length: middleLabelCount },
    (_, i) => (i + 1) * interval,
  );
  const tickIndices =
    !isLoading && dataLength !== 0
      ? [firstIndex, ...middleIndices, lastIndex]
      : [];

  if (isLoading) {
    return (
      <div>
        {title ? (
          <h3 style={style?.title} className="mb-1 ml-1">
            {title}
          </h3>
        ) : null}
        <Skeleton className="h-[350px] w-full rounded-md" />
      </div>
    );
  }

  // Caller still passes the Victory-shaped `style.line.data.stroke`;
  // honour it as the actual `stroke` on the rendered Line.
  const seriesStroke = style?.line?.data?.stroke;

  return (
    <div>
      {title ? (
        <h3 style={style?.title} className="-mb-1 ml-1">
          {title}
        </h3>
      ) : null}
      <ChartFilterBadges filters={activeFilters} style={style?.activeFilters} />
      {dataLength === 0 ? (
        <div className="flex h-[350px] items-center justify-center text-sm text-muted-foreground">
          {noDataMessage}
        </div>
      ) : (
        <ChartContainer config={CHART_CONFIG} className="h-[350px] w-full">
          <RechartsLineChart
            data={data}
            margin={{ top: 16, right: 16, left: 8, bottom: 24 }}
          >
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis
              dataKey="x"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              interval={0}
              tickFormatter={(value, index) => {
                if (showAllTicks) return String(value).replace(" ", "\n");
                if (tickIndices.includes(index)) return value;
                return "";
              }}
              label={
                xLabel
                  ? {
                      value: xLabel,
                      position: "insideBottom",
                      offset: -16,
                      style: { fill: "var(--muted-foreground)" },
                    }
                  : undefined
              }
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              label={
                yLabel
                  ? {
                      value: yLabel,
                      angle: -90,
                      position: "insideLeft",
                      style: { fill: "var(--muted-foreground)" },
                    }
                  : undefined
              }
            />
            <ChartTooltip
              cursor={false}
              content={
                <ChartTooltipContent
                  hideLabel={false}
                  formatter={(value, _name, item) => {
                    const x = item?.payload?.x;
                    const rounded = Math.round(value * 100) / 100;
                    if (tooltipLabel) {
                      return tooltipLabel
                        .replace("{x}", x)
                        .replace("{y}", rounded);
                    }
                    return `${x}: ${rounded}`;
                  }}
                />
              }
            />
            <Line
              dataKey="y"
              type="monotone"
              stroke={seriesStroke || "var(--color-y)"}
              strokeWidth={1.5}
              dot={false}
              activeDot={{ r: 4 }}
              isAnimationActive
            />
          </RechartsLineChart>
        </ChartContainer>
      )}
    </div>
  );
}
